// LLM generated - Model Claude 4 Sonnet
/**
 * Contest Chat Context - Using React's useOptimistic + useTransition
 *
 * This version provides a simplified context that uses the state hook
 * with built-in optimistic updates for both messages and reactions.
 */
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  type PropsWithChildren,
} from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { useContestContext } from '../../context/ContestContext/ContestContext';
import useIsContestCommissioner from '~/domains/commissioner/hooks/useIsContestCommissioner';
import { useContestChatState, type ChatViewer } from './hooks/useContestChatState';
import { isChatSpectator } from './isChatSpectator';
import { ContestChatModalContext } from './ContestChatModals/ContestChatModalContext';
import { reportEvent } from '~/domains/analytics/events';

// The return type from useContestChatState
type UseContestChatStateReturn = ReturnType<typeof useContestChatState>;

/**
 * Context value for ContestChat
 * Simplified: reactions are now handled in the state hook, not in actions
 */
type ContestChatContextValue = UseContestChatStateReturn & {
  contestId: string;
  contestName?: string;
  isCommissioner: boolean;
  user: {
    id: string;
    handle?: string;
    avatarUrl?: string;
  } | null;
  activeTab: 'messages' | 'posts';
  setActiveTab: (tab: 'messages' | 'posts') => void;
  /** Whether chat is open (either desktop side panel or mobile drawer) */
  isChatOpen: boolean;
};

export const ContestChatContext = createContext<ContestChatContextValue | undefined>(undefined);

/**
 * Hook to access ContestChat context
 */
export function useContestChatContext() {
  const context = useContext(ContestChatContext);
  if (!context) {
    throw new Error('useContestChatContext must be used within ContestChatProvider');
  }
  return context;
}

export function ContestChatProvider({ children }: PropsWithChildren) {
  const { user } = useContext(UserContext);
  const { contest, isContestLoading } = useContestContext();

  const { isChatPanelOpen, modals } = useContext(ContestChatModalContext);

  const isMobileChatModalOpen = modals.some((modal) => modal.type === 'ContestChatModal');
  const isChatOpen = isChatPanelOpen || isMobileChatModalOpen;

  const contestId = contest?.id;
  const contestName = contest?.name;
  const isCommissioner = useIsContestCommissioner(contest);

  const [activeTab, setActiveTab] = useState<'messages' | 'posts'>('messages');

  // --- Analytics Tracking (one-time per session) ---
  const hasReportedChatOpenRef = useRef(false);
  const hasReportedPostsViewRef = useRef(false);

  // Reset tracking refs when contest changes
  useEffect(() => {
    hasReportedChatOpenRef.current = false;
    hasReportedPostsViewRef.current = false;
  }, [contestId]);

  // Track first chat open per session
  useEffect(() => {
    if (isChatOpen && !hasReportedChatOpenRef.current && contestId && user?.id) {
      reportEvent('ContestChat > Open', {
        userId: user.id,
        contestId,
        contestName,
        leagueAlias: contest?.league?.alias,
        contestType: contest?.contest_type,
        isCommissioner,
        viewType: isChatPanelOpen ? 'sidePanel' : 'drawer',
      });
      hasReportedChatOpenRef.current = true;
    }
  }, [
    isChatOpen,
    contestId,
    contestName,
    contest?.league?.alias,
    contest?.contest_type,
    isCommissioner,
    isChatPanelOpen,
    user?.id,
  ]);

  // Track first posts tab view per session
  useEffect(() => {
    if (activeTab === 'posts' && !hasReportedPostsViewRef.current && contestId && user?.id) {
      reportEvent('ContestChat > View Posts Tab', {
        userId: user.id,
        contestId,
        contestName,
        leagueAlias: contest?.league?.alias,
        contestType: contest?.contest_type,
      });
      hasReportedPostsViewRef.current = true;
    }
  }, [activeTab, contestId, contestName, contest?.league?.alias, contest?.contest_type, user?.id]);

  // While the contest loads `entries.user` reads undefined, which would classify an
  // entrant as a spectator — and the chat takes no subscription until it knows which
  // of the two it is.
  const viewer: ChatViewer = useMemo(() => {
    if (isContestLoading) return 'unknown';

    return isChatSpectator({ userEntryCount: contest?.entries?.user, isCommissioner })
      ? 'spectator'
      : 'participant';
  }, [isContestLoading, contest?.entries?.user, isCommissioner]);

  // State hook with built-in optimistic updates
  const chatState = useContestChatState({
    contestId,
    contestName,
    user,
    isChatOpen,
    activeTab,
    viewer,
  });

  const value: ContestChatContextValue = useMemo(
    () => ({
      ...chatState,
      contestId,
      contestName,
      isCommissioner,
      user,
      activeTab,
      setActiveTab,
      isChatOpen,
    }),
    [chatState, contestId, contestName, isCommissioner, user, activeTab, setActiveTab, isChatOpen]
  );

  if (!contestId) {
    return null;
  }

  return <ContestChatContext.Provider value={value}>{children}</ContestChatContext.Provider>;
}
